// api.js
import axios from 'axios';

// 환경에 따른 baseURL 설정
const baseURL = process.env.NODE_ENV === 'production' 
  ? 'https://safeai.kids/api'  // 실제 백엔드 서버 URL (API 경로 포함)
  : process.env.REACT_APP_API_URL || 'http://localhost:3001/api'; // Docker 환경에서는 REACT_APP_API_URL 환경변수 사용

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL,
  timeout: 60000, // 60초 타임아웃 (Analytics API 및 이미지 생성용)
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: 토큰 자동 추가
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 개발 환경에서 요청 로깅
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 API 요청:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        hasAuth: !!config.headers.Authorization
      });
    }
    
    return config;
  },
  (error) => {
    console.error('❌ API 요청 에러:', error);
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 에러 처리 및 토큰 만료 처리
apiClient.interceptors.response.use(
  (response) => {
    // 개발 환경에서 응답 로깅
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API 응답:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }
    
    return response;
  },
  (error) => {
    // 토큰 만료 또는 인증 실패 시 자동 로그아웃
    if (error.response?.status === 401) {
      console.warn('🔒 인증 실패 - 토큰 제거');
      localStorage.removeItem('token');

      // /auth/verify 호출에서 401은 단순 토큰 만료이므로 리다이렉트하지 않음
      // AuthContext에서 setUser(null), setLoading(false)로 처리하여 랜딩 페이지 표시
      const isVerifyRequest = error.config?.url?.includes('/auth/verify');

      // verify 요청이 아닌 경우에만 로그인 페이지로 리디렉션
      if (!isVerifyRequest && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    // 개발 환경에서 에러 로깅
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API 응답 에러:', {
        status: error.response?.status,
        url: error.config?.url,
        message: error.response?.data?.error || error.message
      });
    }
    
    return Promise.reject(error);
  }
);

// 토큰 설정 함수
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('🔑 인증 토큰 설정됨');
  } else {
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
    console.log('🔓 인증 토큰 제거됨');
  }
};

// 토큰 제거 함수
export const clearAuthToken = () => {
  localStorage.removeItem('token');
  delete apiClient.defaults.headers.common['Authorization'];
  console.log('🗑️ 인증 토큰 완전 제거됨');
};

// API 엔드포인트 함수들
export const authAPI = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  register: (userData) => apiClient.post('/auth/register', userData),
  verify: () => apiClient.get('/auth/verify'),
  logout: () => apiClient.post('/auth/logout'),
  updateProfile: (profileData) => apiClient.put('/auth/profile', profileData),
  changePassword: (passwordData) => apiClient.put('/auth/change-password', passwordData),
};

export const chatAPI = {
  sendMessage: (messageData) => apiClient.post('/chat/send', messageData),
  getSessions: () => apiClient.get('/sessions'),
  createSession: (sessionData) => apiClient.post('/sessions', sessionData),
  getSession: (sessionId) => apiClient.get(`/sessions/${sessionId}`),
  updateSession: (sessionId, updateData) => apiClient.patch(`/sessions/${sessionId}`, updateData),
  deleteSession: (sessionId) => apiClient.delete(`/sessions/${sessionId}`),
};

export const analyticsAPI = {
  getUserStats: () => apiClient.get('/analytics/user-stats'),
  getClassStats: () => apiClient.get('/analytics/class-stats'),
  getSystemStats: () => apiClient.get('/analytics/system-stats'),
};

export const userAPI = {
  updateProfile: (profileData) => apiClient.put('/user/profile', profileData),
  changePassword: (passwordData) => apiClient.put('/user/change-password', passwordData),
  getProfile: () => apiClient.get('/user/profile'),
  createUser: (userData) => apiClient.post('/user/create', userData),
  getUsers: (params) => apiClient.get('/user/list', { params }),
  updateUser: (userId, userData) => apiClient.put(`/user/${userId}`, userData),
  deleteUser: (userId) => apiClient.delete(`/user/${userId}`),
};

// baseURL export for streaming API calls
export { baseURL };

export default apiClient;
